<template>
	<div :class="classes"
		@click="$emit( 'click', $event )"
		@mouseenter="onMouseEnter"
		@mouseleave="onMouseLeave"
	>
		<div class="number-chip__label-key">{{ label }}</div>
		<div :class="numberClasses">{{ number }}</div>
	</div>
</template>

<script>
export const schemes = Object.freeze( {
	DEFAULT: "BLUE",
	BLUE: "BLUE",
	RED: "RED",
	YELLOW: "YELLOW",
	MONOCHROME: "MONOCHROME",
} );

export default {
	name: "NumberChip",
	emits: [
		"mouseenter",
		"mouseleave",
		"click",
	],
	props: {
		className: {
			type: String,
			default: "",
		},
		label: {
			type: String,
			required: true,
		},
		number: {
			type: [ Number, String, ],
			required: true,
		},
		isHighlighted: {
			type: Boolean,
			default: true,
		},
		scheme: {
			type: String,
			default: schemes.DEFAULT,
		},
	},
	data() {
		return {
			isHovering: false,
		};
	},
	computed: {
		classes() {
			let chosenScheme = this.scheme;
			if ( !Object.keys( schemes ).includes( chosenScheme ) ) {
				chosenScheme = schemes.DEFAULT;
			}
			const lowercaseScheme = chosenScheme.toLowerCase();
			return {
				"number-chip": true,
				[ `number-chip__${ lowercaseScheme }-scheme` ]: true,
				[ `number-chip__${ lowercaseScheme }-scheme--hovering` ]: this.isHovering && !this.isHighlighted,
				[ `number-chip__${ lowercaseScheme }-scheme--highlighted` ]: this.isHighlighted,
				[ this.className ]: !!this.className,
			};
		},
		numberClasses() {
			return {
				"number-chip__number": true,
			};
		},
	},
	methods: {
		onMouseEnter( e ) {
			this.isHovering = true;
			this.$emit( "mouseenter", e );
		},
		onMouseLeave( e ) {
			this.isHovering = false;
			this.$emit( "mouseleave", e );
		},
	},
};
</script>

<style lang="scss" scoped>
.number-chip {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: space-between;
	border-radius: .5rem;
	padding: .25rem .5rem;
	@include font-and-line-for(0);
	font-weight: 600;
	cursor: pointer;
	width: 100%;
}

.number-chip__label-key {
	flex-grow: 1;
	text-align: left;
	white-space: nowrap;
}

.number-chip__number {
	@include font-and-line-for(0);
	border-radius: 1.5rem;
	font-style: normal;
	font-weight: 600;
	padding: 0 .5rem;
	margin-left: .5rem;
}

//
// blue scheme
//
.number-chip__blue-scheme {
	background-color: $number-chip-blue-scheme-label-background-color;
	color: $number-chip-blue-scheme-label-font-color;

	.number-chip__number {
		background-color: $number-chip-blue-scheme-number-background-color;
		color: $number-chip-blue-scheme-number-font-color;
	}
}

.number-chip__blue-scheme--hovering {
	background-color: $number-chip-blue-scheme-label-background-color--hover;
	color: $number-chip-blue-scheme-label-font-color--hover;

	.number-chip__number {
		background-color: $number-chip-blue-scheme-number-background-color--hover;
		color: $number-chip-blue-scheme-number-font-color--hover;
	}
}

.number-chip__blue-scheme--highlighted {
	background-color: $number-chip-blue-scheme-label-background-color--highlighted;
	color: $number-chip-blue-scheme-label-font-color--highlighted;

	.number-chip__number {
		background-color: $number-chip-blue-scheme-number-background-color--highlighted;
		color: $number-chip-blue-scheme-number-font-color--highlighted;
	}
}

//
// red scheme
//
.number-chip__red-scheme {
	background-color: $number-chip-red-scheme-label-background-color;
	color: $number-chip-red-scheme-label-font-color;

	.number-chip__number {
		background-color: $number-chip-red-scheme-number-background-color;
		color: $number-chip-red-scheme-number-font-color;
	}
}

.number-chip__red-scheme--hovering {
	background-color: $number-chip-red-scheme-label-background-color--hover;
	color: $number-chip-red-scheme-label-font-color--hover;

	.number-chip__number {
		background-color: $number-chip-red-scheme-number-background-color--hover;
		color: $number-chip-red-scheme-number-font-color--hover;
	}
}

.number-chip__red-scheme--highlighted {
	background-color: $number-chip-red-scheme-label-background-color--highlighted;
	color: $number-chip-red-scheme-label-font-color--highlighted;

	.number-chip__number {
		background-color: $number-chip-red-scheme-number-background-color--highlighted;
		color: $number-chip-red-scheme-number-font-color--highlighted;
	}
}

//
// yellow scheme
//
.number-chip__yellow-scheme {
	background-color: $number-chip-yellow-scheme-label-background-color;
	color: $number-chip-yellow-scheme-label-font-color;

	.number-chip__number {
		background-color: $number-chip-yellow-scheme-number-background-color;
		color: $number-chip-yellow-scheme-number-font-color;
	}
}

.number-chip__yellow-scheme--hovering {
	background-color: $number-chip-yellow-scheme-label-background-color--hover;
	color: $number-chip-yellow-scheme-label-font-color--hover;

	.number-chip__number {
		background-color: $number-chip-yellow-scheme-number-background-color--hover;
		color: $number-chip-yellow-scheme-number-font-color--hover;
	}
}

.number-chip__yellow-scheme--highlighted {
	background-color: $number-chip-yellow-scheme-label-background-color--highlighted;
	color: $number-chip-yellow-scheme-label-font-color--highlighted;

	.number-chip__number {
		background-color: $number-chip-yellow-scheme-number-background-color--highlighted;
		color: $number-chip-yellow-scheme-number-font-color--highlighted;
	}
}

//
// monochrome scheme
//
.number-chip__monochrome-scheme {
	background-color: $number-chip-monochrome-scheme-label-background-color;
	color: $number-chip-monochrome-scheme-label-font-color;

	.number-chip__number {
		background-color: $number-chip-monochrome-scheme-number-background-color;
		color: $number-chip-monochrome-scheme-number-font-color;
	}
}

.number-chip__monochrome-scheme--hovering {
	background-color: $number-chip-monochrome-scheme-label-background-color--hover;
	color: $number-chip-monochrome-scheme-label-font-color--hover;

	.number-chip__number {
		background-color: $number-chip-monochrome-scheme-number-background-color--hover;
		color: $number-chip-monochrome-scheme-number-font-color--hover;
	}
}

.number-chip__monochrome-scheme--highlighted {
	background-color: $number-chip-monochrome-scheme-label-background-color--highlighted;
	color: $number-chip-monochrome-scheme-label-font-color--highlighted;

	.number-chip__number {
		background-color: $number-chip-monochrome-scheme-number-background-color--highlighted;
		color: $number-chip-monochrome-scheme-number-font-color--highlighted;
	}
}
</style>
